import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Component, DestroyRef, OnInit, inject, ChangeDetectionStrategy } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { MatTableModule } from "@angular/material/table";
import { OuiButtonModule } from "omnium-ui/button";
import { OuiDialogModule } from "omnium-ui/dialog";
import { OuiCheckbox } from "omnium-ui/form-field";
import { OuiSnackbarService } from "omnium-ui/snackbar";
import { OuiTableModule } from "omnium-ui/table";
import { OuiTagModule } from "omnium-ui/tag";
import {
  InvestorViewerPerson,
  InvestorPersonCorrection,
  InvestorPersonCorrectionRequest,
  InvestorPersonCorrectionSuggestionMessageLevel,
  InvestorPersonRelationKind,
  ViewerCoupleRelation,
  ViewerFamilyRelation,
  ViewerOtherRelation,
} from "../../../../../../core/models/api.models";
import { DoublonsBusterApiService } from "../../../../../../core/services/doublons-buster-api.service";
import {
  formatDate,
  formatIdentity,
  formatPersonLabel,
  formatText,
} from "../../investor-viewer-format.utils";

type CorrectionStep = "select-persons" | "select-relations" | "summary";
type RelationKind = "Couple" | "Famille" | "Autre";

type InvestorViewerPersonCorrectionDialogData = {
  investorId: number;
  persons: InvestorViewerPerson[];
};

type RelationTransferRow = {
  id: string;
  sourcePerson: InvestorViewerPerson;
  relationKind: InvestorPersonRelationKind;
  kind: RelationKind;
  relationId?: number | null;
  relationTypeCode?: string | null;
  relatedPersonId?: number | null;
  relatedPersonLabel: string;
  arianeId?: number | null;
  canTransfer: boolean;
  selectedForTransfer: boolean;
};

@Component({
  selector: "dbw-investor-viewer-person-correction-dialog",
  imports: [
    MatTableModule,
    OuiButtonModule,
    OuiCheckbox,
    OuiDialogModule,
    OuiTableModule,
    OuiTagModule,
  ],
  templateUrl: "./investor-viewer-person-correction-dialog.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./investor-viewer-person-correction-dialog.component.scss",
})
export class InvestorViewerPersonCorrectionDialogComponent implements OnInit {
  private readonly dialogRef = inject(
    MatDialogRef<
      InvestorViewerPersonCorrectionDialogComponent,
      { refreshInspection: boolean } | undefined
    >,
  );
  private readonly destroyRef = inject(DestroyRef);
  private readonly apiService = inject(DoublonsBusterApiService);
  private readonly snackbarService = inject(OuiSnackbarService);
  private readonly data =
    inject<InvestorViewerPersonCorrectionDialogData>(MAT_DIALOG_DATA);

  protected readonly investorId = this.data.investorId;
  protected readonly persons = [...this.data.persons];
  protected readonly personColumns = [
    "delete",
    "identity",
    "birth",
    "contact",
    "relations",
  ];
  protected readonly relationColumns = [
    "transfer",
    "source",
    "kind",
    "relation",
    "relatedPerson",
    "arianeId",
  ];
  protected readonly formatText = formatText;
  protected readonly formatDate = formatDate;
  protected readonly formatPersonLabel = formatPersonLabel;

  protected step: CorrectionStep = "select-persons";
  protected readonly personIdsToDelete = new Set<number>();
  protected suggestionMessage = "Recherche d'une pré-sélection en cours...";
  protected suggestionLevel: InvestorPersonCorrectionSuggestionMessageLevel =
    "info";
  protected initializing = true;
  protected relationRows: RelationTransferRow[] = [];
  protected selectedRelationCount = 0;
  protected executing = false;
  protected executionError?: string;
  protected executionResult?: InvestorPersonCorrection;
  private personSelectionTouched = false;

  protected get canValidatePersonSelection(): boolean {
    return (
      !this.initializing &&
      this.keptPersons.length === 1 &&
      this.personIdsToDelete.size > 0
    );
  }

  protected get keptPersons(): InvestorViewerPerson[] {
    return this.persons.filter(
      (person) => !this.personIdsToDelete.has(person.personId),
    );
  }

  protected get personsToDelete(): InvestorViewerPerson[] {
    return this.persons.filter((person) =>
      this.personIdsToDelete.has(person.personId),
    );
  }

  protected get keptPerson(): InvestorViewerPerson | undefined {
    return this.keptPersons[0];
  }

  protected get remainingPersonCount(): number {
    return this.keptPersons.length;
  }

  protected get hasRelationsToTransfer(): boolean {
    return this.relationRows.some((row) => row.canTransfer);
  }

  protected get transferableRelationCount(): number {
    return this.relationRows.filter((row) => row.canTransfer).length;
  }

  protected get suggestionTagStatus(): "completed" | "pending" | "error" {
    switch (this.suggestionLevel) {
      case "success":
        return "completed";
      case "error":
        return "error";
      default:
        return "pending";
    }
  }

  ngOnInit(): void {
    this.apiService
      .getInvestorPersonCorrectionSuggestion(this.investorId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (suggestion) => {
          this.initializing = false;
          this.suggestionMessage = suggestion.message;
          this.suggestionLevel = suggestion.messageLevel;

          if (
            !this.personSelectionTouched &&
            suggestion.hasSuggestedSelection
          ) {
            this.applySuggestedDeletionSelection(
              suggestion.suggestedDeletedPersonIds,
            );
          }
        },
        error: (error) => {
          this.initializing = false;
          this.suggestionLevel = "error";
          this.suggestionMessage =
            error?.error?.detail ??
            "La pré-sélection a échoué. La sélection reste manuelle.";
        },
      });
  }

  protected togglePersonDeletion(personId: number, checked: boolean): void {
    this.personSelectionTouched = true;

    if (checked) {
      this.personIdsToDelete.add(personId);
    } else {
      this.personIdsToDelete.delete(personId);
    }
  }

  protected validatePersonSelection(): void {
    if (!this.canValidatePersonSelection) {
      return;
    }

    this.executionError = undefined;
    this.executionResult = undefined;
    this.relationRows = this.buildRelationRows(
      this.personsToDelete,
      this.personIdsToDelete,
      this.keptPerson?.personId,
    );
    this.updateSelectedRelationCount();
    this.step = this.relationRows.length > 0 ? "select-relations" : "summary";
  }

  protected toggleRelationTransfer(
    row: RelationTransferRow,
    checked: boolean,
  ): void {
    if (!row.canTransfer) {
      return;
    }

    row.selectedForTransfer = checked;
    this.updateSelectedRelationCount();
  }

  protected backToPersonSelection(): void {
    this.step = "select-persons";
    this.relationRows = [];
    this.selectedRelationCount = 0;
  }

  protected validateRelationSelection(): void {
    this.executionError = undefined;
    this.executionResult = undefined;
    this.step = "summary";
  }

  protected backFromSummary(): void {
    this.executionError = undefined;
    this.executionResult = undefined;
    this.step =
      this.relationRows.length > 0 ? "select-relations" : "select-persons";
  }

  protected close(refreshInspection = false): void {
    this.dialogRef.close({ refreshInspection });
  }

  protected getRelationCount(person: InvestorViewerPerson): number {
    return (
      (person.coupleRelations?.length ?? 0) +
      (person.familyRelations?.length ?? 0) +
      (person.otherRelations?.length ?? 0)
    );
  }

  protected getContactSummary(person: InvestorViewerPerson): string {
    return [
      `${person.addresses?.length ?? 0} adresse(s)`,
      `${person.phones?.length ?? 0} téléphone(s)`,
      `${person.emails?.length ?? 0} email(s)`,
    ].join(" • ");
  }

  protected getRegulatorySummary(person: InvestorViewerPerson): string {
    return (
      [
        person.isPoliticallyExposed === true ? "PPE" : null,
        person.isUsCitizenOrResident === true ? "US person" : null,
      ]
        .filter((value): value is string => !!value)
        .join(" • ") || "-"
    );
  }

  protected formatRelatedPerson(row: RelationTransferRow): string {
    if (!row.relatedPersonId) {
      return row.relatedPersonLabel;
    }

    const idLabel = `#${row.relatedPersonId}`;
    return row.relatedPersonLabel.includes(idLabel)
      ? row.relatedPersonLabel
      : `${row.relatedPersonLabel} (${idLabel})`;
  }

  protected executeCorrection(): void {
    const keptPerson = this.keptPerson;
    if (!keptPerson || this.executing) {
      return;
    }

    const request: InvestorPersonCorrectionRequest = {
      keptPersonId: keptPerson.personId,
      deletedPersonIds: this.personsToDelete.map((person) => person.personId),
      relationTransfers: this.relationRows
        .filter(
          (row) =>
            row.canTransfer && row.selectedForTransfer && row.relatedPersonId,
        )
        .map((row) => ({
          relationKind: row.relationKind,
          sourcePersonId: row.sourcePerson.personId,
          relationId: row.relationId ?? null,
          relatedPersonId: row.relatedPersonId!,
          relationTypeCode: row.relationTypeCode ?? null,
        })),
    };

    this.executing = true;
    this.executionError = undefined;

    this.apiService
      .executeInvestorPersonCorrection(this.investorId, request)
      .subscribe({
        next: (result) => {
          this.executing = false;
          this.executionResult = result;

          if (result.success) {
            this.snackbarService.open(result.message, "success");
          } else {
            this.snackbarService.open(result.message, "error");
          }
        },
        error: (error) => {
          this.executing = false;
          this.executionResult = undefined;
          this.executionError =
            error?.error?.detail ??
            "Impossible d’exécuter la correction des personnes physiques.";
          this.snackbarService.open(
            this.executionError ??
              "Impossible d’exécuter la correction des personnes physiques.",
            "error",
          );
        },
      });
  }

  private buildRelationRows(
    personsToDelete: InvestorViewerPerson[],
    deletedPersonIds: ReadonlySet<number>,
    keptPersonId?: number,
  ): RelationTransferRow[] {
    const rows = personsToDelete.flatMap((person) => [
      ...this.buildCoupleRelationRows(person, deletedPersonIds, keptPersonId),
      ...this.buildFamilyRelationRows(person, deletedPersonIds, keptPersonId),
      ...this.buildOtherRelationRows(person, deletedPersonIds, keptPersonId),
    ]);

    return this.deduplicateRelationRows(rows);
  }

  private buildCoupleRelationRows(
    person: InvestorViewerPerson,
    deletedPersonIds: ReadonlySet<number>,
    keptPersonId?: number,
  ): RelationTransferRow[] {
    return (person.coupleRelations ?? []).map((relation, index) => {
      const canTransfer =
        relation.relatedPersonId !== null &&
        relation.relatedPersonId !== undefined &&
        relation.relatedPersonId !== keptPersonId &&
        !deletedPersonIds.has(relation.relatedPersonId);

      return {
        id: `couple-${person.personId}-${index}-${relation.relatedPersonId ?? "unknown"}`,
        sourcePerson: person,
        relationKind: "Couple",
        kind: "Couple",
        relationId: null,
        relationTypeCode: relation.relationTypeCode,
        relatedPersonId: relation.relatedPersonId,
        relatedPersonLabel: this.getRelatedPersonLabel(relation),
        arianeId: relation.arianeId,
        canTransfer,
        selectedForTransfer: canTransfer,
      };
    });
  }

  private buildFamilyRelationRows(
    person: InvestorViewerPerson,
    deletedPersonIds: ReadonlySet<number>,
    keptPersonId?: number,
  ): RelationTransferRow[] {
    return (person.familyRelations ?? []).map((relation) => {
      const canTransfer =
        relation.relatedPersonId !== null &&
        relation.relatedPersonId !== undefined &&
        relation.relatedPersonId !== keptPersonId &&
        !deletedPersonIds.has(relation.relatedPersonId);

      return {
        id: `family-${person.personId}-${relation.familyRelationId}`,
        sourcePerson: person,
        relationKind: "Famille",
        kind: "Famille",
        relationId: relation.familyRelationId,
        relationTypeCode: relation.relationTypeCode,
        relatedPersonId: relation.relatedPersonId,
        relatedPersonLabel: this.getRelatedPersonLabel(relation),
        arianeId: relation.arianeId,
        canTransfer,
        selectedForTransfer: canTransfer,
      };
    });
  }

  private buildOtherRelationRows(
    person: InvestorViewerPerson,
    deletedPersonIds: ReadonlySet<number>,
    keptPersonId?: number,
  ): RelationTransferRow[] {
    return (person.otherRelations ?? []).map((relation) => {
      const canTransfer =
        relation.relatedPersonId !== null &&
        relation.relatedPersonId !== undefined &&
        relation.relatedPersonId !== keptPersonId &&
        !deletedPersonIds.has(relation.relatedPersonId);

      return {
        id: `other-${person.personId}-${relation.otherRelationId}`,
        sourcePerson: person,
        relationKind: "Autre",
        kind: "Autre",
        relationId: relation.otherRelationId,
        relationTypeCode: relation.relationTypeCode,
        relatedPersonId: relation.relatedPersonId,
        relatedPersonLabel: this.getRelatedPersonLabel(relation),
        arianeId: relation.arianeId,
        canTransfer,
        selectedForTransfer: canTransfer,
      };
    });
  }

  private getRelatedPersonLabel(
    relation: ViewerCoupleRelation | ViewerFamilyRelation | ViewerOtherRelation,
  ): string {
    const relatedPerson = relation.relatedPersonId
      ? this.persons.find(
          (person) => person.personId === relation.relatedPersonId,
        )
      : undefined;

    if (relatedPerson) {
      return formatIdentity(
        relatedPerson.lastName,
        relatedPerson.firstName,
        `Personne #${relatedPerson.personId}`,
      );
    }

    return formatIdentity(
      relation.relatedLastName,
      relation.relatedFirstName,
      relation.relatedPersonId
        ? `Personne #${relation.relatedPersonId}`
        : "Personne non renseignée",
    );
  }

  private updateSelectedRelationCount(): void {
    this.selectedRelationCount = this.relationRows.filter(
      (row) => row.canTransfer && row.selectedForTransfer,
    ).length;
  }

  private deduplicateRelationRows(
    rows: readonly RelationTransferRow[],
  ): RelationTransferRow[] {
    const rowsByKey = new Map<string, RelationTransferRow>();

    rows.forEach((row) => {
      const key = this.buildRelationRowKey(row);

      if (!rowsByKey.has(key)) {
        rowsByKey.set(key, {
          ...row,
          id: key,
        });
      }
    });

    return Array.from(rowsByKey.values());
  }

  private buildRelationRowKey(row: RelationTransferRow): string {
    return [
      row.relationKind,
      row.sourcePerson.personId,
      row.relationId ?? "-",
      row.relationTypeCode?.trim() ?? "-",
      row.relatedPersonId ?? "-",
      row.arianeId ?? "-",
    ].join("|");
  }

  private applySuggestedDeletionSelection(
    personIdsToDelete: readonly number[],
  ): void {
    this.personIdsToDelete.clear();

    personIdsToDelete.forEach((personId) => {
      if (this.persons.some((person) => person.personId === personId)) {
        this.personIdsToDelete.add(personId);
      }
    });
  }
}
